import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast as sonnerToast } from 'sonner';
import { useToast } from '@/app/_module/hooks/use-toast';
import { useUserContext } from '@/app/_module/store/contexts/userContext/userContexts';
import { leadAssignmentService } from '../services/service';
import api from '@/app/_module/services/api';
import { getApiUrl } from '@/app/_module/services/config';
import { endpoint } from '@/app/_module/services/endpoints';
import { handleApiCall } from '@/app/_module/utils/apiResponseHandler';

// Backend accepts up to 5,000 per unassign call (MAX_BULK_UNASSIGN); we send
// 2,000 at a time, same as assign. Frontend splits larger selections into
// sequential batches so owners can unassign an entire 5k+ list in one action.
const BACKEND_BATCH_SIZE = 2000;
const MAX_BULK_UNASSIGN_TOTAL = 25000; // = the CSV/Excel import cap (MAX_IMPORT_ROWS)

/** Extra context for optimistic updates when unassigning leads */
export interface UnassignLeadContext {
  /** Status of each lead being unassigned (for optimistic byStatus decrement) */
  leadStatuses?: string[];
  /** Agent ID the leads are being unassigned from (for optimistic activeLeadsCount decrement) */
  agentId?: string;
}

interface UnassignMutationInput {
  leadIds: string[];
  context?: UnassignLeadContext;
  /** Also unassign sold/closed leads (kept with their agent by default). */
  includeClosed?: boolean;
}

export const useUnassignLead = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user } = useUserContext();
  const currentAgencyId = user.currentAgency?.agency?.id;

  return useMutation({
    mutationFn: async ({ leadIds, includeClosed = false }: UnassignMutationInput) => {
      const total = leadIds?.length ?? 0;
      if (total === 0) {
        throw new Error('No leads selected to unassign.');
      }
      if (total > MAX_BULK_UNASSIGN_TOTAL) {
        throw new Error(
          `You can unassign at most ${MAX_BULK_UNASSIGN_TOTAL.toLocaleString()} leads in a single action. You selected ${total.toLocaleString()} — please narrow the selection.`,
        );
      }

      if (total <= BACKEND_BATCH_SIZE) {
        return await leadAssignmentService.unassignLead(leadIds, includeClosed);
      }

      // Large selections: sequential batches of BACKEND_BATCH_SIZE with live progress.
      const toastId = sonnerToast.loading('Unassigning leads', {
        description: `0 of ${total.toLocaleString()} unassigned…`,
      });

      const failedLeadIds: string[] = [];
      let unassignedSoFar = 0;
      let keptIds: string[] = [];

      for (let i = 0; i < total; i += BACKEND_BATCH_SIZE) {
        const slice = leadIds.slice(i, i + BACKEND_BATCH_SIZE);
        try {
          const r = await leadAssignmentService.unassignLead(slice, includeClosed);
          const kept = r?.skippedClosedIds ?? [];
          keptIds = keptIds.concat(kept);
          unassignedSoFar += r?.unassignedCount ?? slice.length - kept.length;
        } catch (batchErr) {
          failedLeadIds.push(...slice);
        }
        sonnerToast.loading('Unassigning leads', {
          id: toastId,
          description: `${unassignedSoFar.toLocaleString()} of ${total.toLocaleString()} unassigned${failedLeadIds.length > 0 ? ` · ${failedLeadIds.length.toLocaleString()} failed` : ''}…`,
        });
      }

      sonnerToast.dismiss(toastId);

      if (failedLeadIds.length > 0 && unassignedSoFar === 0) {
        throw new Error(`All ${total.toLocaleString()} unassignments failed. Please try again.`);
      }
      if (failedLeadIds.length > 0) {
        sonnerToast.warning(
          `${unassignedSoFar.toLocaleString()} unassigned, ${failedLeadIds.length.toLocaleString()} failed`,
          { description: 'The failed leads are still assigned. Try again for just those.' },
        );
      }
      return { success: true, message: '', unassignedCount: unassignedSoFar, skippedClosedIds: keptIds };
    },
    onMutate: async ({ leadIds, context: extraContext }: UnassignMutationInput) => {
      // Optimistically update status-tracking and agent workload so UI updates instantly
      await queryClient.cancelQueries({ queryKey: ['status-tracking'] });
      await queryClient.cancelQueries({ queryKey: ['agentWorkload'] });

      const previousTracking = queryClient.getQueryData(['status-tracking']);
      // Use exact key match: ["agentWorkload", agencyId]
      const workloadKey = ['agentWorkload', currentAgencyId];
      const previousWorkload = queryClient.getQueryData(workloadKey);

      const count = leadIds.length;

      queryClient.setQueryData(['status-tracking'], (old: any) => {
        if (!old?.summary) return old;

        // Optimistically decrement byStatus counts if we know the lead statuses
        const updatedByStatus = old.byStatus ? { ...old.byStatus } : {};
        if (extraContext?.leadStatuses) {
          for (const status of extraContext.leadStatuses) {
            const key = status || 'New lead';
            if (updatedByStatus[key]) {
              updatedByStatus[key] = Math.max(0, updatedByStatus[key] - 1);
            }
          }
        }

        return {
          ...old,
          summary: {
            ...old.summary,
            assigned: Math.max(0, (old.summary.assigned || 0) - count),
            unassigned: (old.summary.unassigned || 0) + count,
          },
          byStatus: updatedByStatus,
        };
      });

      // Optimistically decrement agent's activeLeadsCount if we know which agent
      if (extraContext?.agentId) {
        queryClient.setQueryData(workloadKey, (old: any) => {
          if (!old?.agents) return old;
          return {
            ...old,
            agents: old.agents.map((agent: any) => {
              if (agent.id === extraContext.agentId || agent.agentId === extraContext.agentId) {
                return {
                  ...agent,
                  currentLeads: Math.max(0, (agent.currentLeads || 0) - count),
                  activeLeadsCount: Math.max(0, (agent.activeLeadsCount || 0) - count),
                };
              }
              return agent;
            }),
          };
        });
      }

      return { previousTracking, previousWorkload };
    },
    onSuccess: (result: any, { leadIds }: UnassignMutationInput) => {
      // Sold/closed leads stay with their agent unless "Include sold/closed leads" was
      // ticked. Say so: a flat "success" while nothing moved read as "unassign is broken".
      const kept = result?.skippedClosedIds?.length ?? 0;
      const done = result?.unassignedCount ?? leadIds.length - kept;
      toast(
        done === 0 && kept > 0
          ? {
              title: "Nothing unassigned",
              description: `${kept === 1 ? 'That lead is' : `All ${kept} are`} sold or closed and stay with their agent. Tick "Include sold/closed leads" to move them too.`,
            }
          : {
              title: "Success",
              description: kept
                ? `${done} unassigned · ${kept} sold/closed stayed with their agent. To move them too, select them, click Unassign and tick "Include sold/closed leads".`
                : "Lead(s) unassigned successfully",
            },
      );

      window.dispatchEvent(new CustomEvent('lead-unassigned'));
    },
    onError: (error: any, _input, context) => {
      // Roll back optimistic updates on error
      if (context?.previousTracking) {
        queryClient.setQueryData(['status-tracking'], context.previousTracking);
      }
      if (context?.previousWorkload) {
        queryClient.setQueryData(['agentWorkload', currentAgencyId], context.previousWorkload);
      }
      toast({
        title: "Error",
        description: error?.message || "Failed to unassign lead(s)",
        variant: "destructive",
      });
    },
    onSettled: async () => {
      // Invalidate non-workload queries immediately
      queryClient.invalidateQueries({ queryKey: ['unassignedLeads'] });
      queryClient.invalidateQueries({ queryKey: ['allLeads'] });
      queryClient.invalidateQueries({ queryKey: ['agentLeads'] }); // per-agent drill-in (AgentWorkload row expand)
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['status-tracking'] });
      queryClient.invalidateQueries({ queryKey: ['lead-stats'] });

      // Fetch workload with ?fresh=true to bypass ALL backend caches
      try {
        const freshUrl = getApiUrl(endpoint.agency.getAgentsWorkload(currentAgencyId!)) + '?limit=1000&fresh=true';
        const freshResult: any = await handleApiCall(async () =>
          api.get({ url: freshUrl, auth: true })
        );

        let agents: any[] = [];
        if (Array.isArray(freshResult)) {
          agents = freshResult;
        } else if (freshResult?.data?.data?.items) {
          agents = freshResult.data.data.items;
        } else if (freshResult?.data?.items) {
          agents = freshResult.data.items;
        } else if (freshResult?.data && Array.isArray(freshResult.data)) {
          agents = freshResult.data;
        } else if (freshResult?.items) {
          agents = freshResult.items;
        } else {
          const d = freshResult?.data || freshResult || {};
          agents = d.items || d.agents || (Array.isArray(d) ? d : []);
        }

        const formatTime = (t: any): string => {
          if (typeof t === 'string') return t;
          if (typeof t === 'number') {
            if (t === 0) return '0m';
            if (t < 60) return `${Math.round(t)}s`;
            return `${Math.round(t / 60)}m`;
          }
          return '0m';
        };

        const mapped = agents.map((a: any) => {
          const uid = a.id || a.userId || a.agentId;
          return {
            id: uid,
            agentId: uid,
            name: a.name || a.agentName || 'Unknown Agent',
            agentName: a.name || a.agentName || 'Unknown Agent',
            email: a.email || '',
            agencyId: a.agencyId || undefined,
            agencyName: a.agencyName || undefined,
            currentLeads: a.currentLeads || a.activeLeadsCount || 0,
            activeLeadsCount: a.currentLeads || a.activeLeadsCount || 0,
            ownedLeads: a.ownedLeads ?? 0,
            completedLeads: a.completedLeads || 0,
            maxLeads: a.maxLeads || 0,
            leadGoal: a.leadGoal ?? undefined,
            status: a.status || 'active',
            lastActivity: a.lastActivity || a.lastActivityAt || undefined,
            conversionRate: a.conversionRate || 0,
            avgResponseTime: formatTime(a.avgResponseTime),
            assignedBreakdown: Array.isArray(a.assignedBreakdown) ? a.assignedBreakdown : [],
          };
        });

        queryClient.setQueryData(['agentWorkload', currentAgencyId], { agents: mapped });
      } catch {
        queryClient.invalidateQueries({ queryKey: ['agentWorkload'] });
      }
    },
  });
};
